<template>
  <v-progress-linear v-if="loading" indeterminate />
  <div v-else>
    <span v-if="addingEditPermissions"
      >{{ itemType }}s here affect all members of this group. Here, you can
      grant edit permission for {{ itemType.toLowerCase() }}s for group
      members.</span
    >
    <span v-else-if="groupId">
      {{ itemType }}s here affect all members of this group. Here, you may allow
      denylisted {{ itemType.toLowerCase() }}s to be seen.
    </span>
    <div class="d-flex mt-2">
      <router-link :to="addUrl">
        <v-btn color="primary" class="mr-3 test-add">
          <span> <v-icon>mdi-plus</v-icon>Add {{ itemType }}s</span>
        </v-btn>
      </router-link>
      <OareDialog
        v-model="confirmRemoveDialog"
        title="Confirm Removal"
        cancelText="No, don't remove"
        submitText="Yes, remove"
        @submit="removeListItems"
        :submitLoading="removeItemsLoading"
      >
        {{ confirmRemoveMessage }}
        <ul>
          <li v-for="item in selectedItems" :key="item.uuid">
            {{ item.name }}
          </li>
        </ul>
      </OareDialog>
      <v-menu>
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            color="info"
            class="ml-3 test-actions"
            :disabled="selectedItems.length < 1"
            data-actions-btn
            >Actions</v-btn
          >
        </template>
        <v-list>
          <v-list-item @click="confirmRemoveDialog = true">
            <v-list-item-title class="test-remove-items"
              >Remove {{ itemType }}s</v-list-item-title
            >
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <v-data-table
      :headers="listHeaders"
      :items="items"
      item-value="uuid"
      return-object
      :search="search"
      :page="currentPage"
      @update:page="updatePage"
      :items-per-page="currentRows"
      @update:items-per-page="updateRows"
      class="mt-3"
      show-select
      v-model="selectedItems"
    >
      <template #[`item.name`]="{ item }">
        <router-link
          v-if="item.hasEpigraphy || itemType === 'Collection'"
          :to="`${itemLink}${item.uuid}`"
          class="test-item-name"
          >{{ item.name }}</router-link
        >
        <span v-else>{{ item.name }}</span>
      </template>
      <template #[`item.url`]="{ item }">
        <v-img :src="item.url" max-height="150" max-width="250"></v-img>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  Ref,
  onMounted,
  computed,
  PropType,
  watch,
} from 'vue';
import { PermissionsListType, DenylistAllowlistItem } from '@oare/types';
import { DataTableHeader } from 'vuetify';
import sl from '@/serviceLocator';

export default defineComponent({
  props: {
    groupId: {
      type: String,
      required: false,
    },
    search: {
      type: String,
      required: false,
    },
    itemType: {
      type: String as PropType<PermissionsListType>,
      required: true,
    },
    // The public denylist fetchers ignore the group id argument.
    getItems: {
      type: Function as PropType<
        (groupId: number) => Promise<DenylistAllowlistItem[]>
      >,
      required: true,
    },
    removeItems: {
      type: Function as PropType<
        (uuids: string[], groupId: number) => Promise<void>
      >,
      required: true,
    },
    addingEditPermissions: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
    rows: {
      type: Number,
      default: 10,
    },
  },
  emits: ['update:page', 'update:rows'],
  setup(props, { emit }) {
    const { groupId, itemType, getItems, removeItems, addingEditPermissions } =
      props;
    const actions = sl.get('globalActions');

    // Paging works on its own; a parent that binds page/rows (to keep them in
    // the URL) still gets every change and can set them.
    const currentPage = ref(props.page);
    const currentRows = ref(props.rows);
    watch(
      () => props.page,
      value => {
        currentPage.value = value;
      }
    );
    watch(
      () => props.rows,
      value => {
        currentRows.value = value;
      }
    );
    const updatePage = (value: number) => {
      currentPage.value = value;
      emit('update:page', value);
    };
    const updateRows = (value: number) => {
      currentRows.value = value;
      emit('update:rows', value);
    };

    const listHeaders: Ref<DataTableHeader[]> = ref(
      itemType === 'Image'
        ? [
            { title: 'Name', key: 'name', width: '30%' },
            { title: 'Image', key: 'url', width: '70%' },
          ]
        : [{ title: 'Name', key: 'name' }]
    );

    const items: Ref<DenylistAllowlistItem[]> = ref([]);
    const selectedItems: Ref<DenylistAllowlistItem[]> = ref([]);

    const addUrl = computed(() => {
      if (groupId) {
        return addingEditPermissions
          ? `/admin/add_edit/${itemType.toLowerCase()}s/${groupId}`
          : `/admin/add_allowlist/${itemType.toLowerCase()}s/${groupId}`;
      }
      return `/admin/add_denylist/${itemType.toLowerCase()}s`;
    });
    const itemLink = computed(() =>
      itemType === 'Collection' ? '/collections/name/' : '/epigraphies/'
    );

    const loading = ref(true);
    const confirmRemoveDialog = ref(false);
    const removeItemsLoading = ref(false);

    const confirmRemoveMessage = computed(() => {
      if (groupId) {
        return `Are you sure you want to remove the following
        ${itemType.toLowerCase()}(s) from this group? Members of this group
        will no longer be able to view this ${itemType.toLowerCase()} if it is denylisted.`;
      } else {
        return `The following ${itemType.toLowerCase()}(s) will be removed
        from the public denylist and will be visible to all users. Are you sure you want to remove them?`;
      }
    });

    onMounted(async () => {
      try {
        items.value = await getItems(Number(groupId));
        items.value = items.value.sort((a, b) =>
          a.name && b.name ? a.name.localeCompare(b.name) : -1
        );
      } catch (err) {
        actions.showErrorSnackbar(
          `Error loading group ${itemType}(s). Please try again.`,
          err as Error
        );
      } finally {
        loading.value = false;
      }
    });

    const removeListItems = async () => {
      try {
        removeItemsLoading.value = true;
        let uuidsToRemove = selectedItems.value.map(item => item.uuid);
        await removeItems(uuidsToRemove, Number(groupId));
        items.value = items.value.filter(
          item => !uuidsToRemove.includes(item.uuid)
        );
        actions.showSnackbar(
          `Successfully removed ${itemType.toLowerCase()}(s).`
        );
        selectedItems.value = [];
      } catch (err) {
        actions.showErrorSnackbar(
          `Error removing ${itemType.toLowerCase()}(s). Please try again.`,
          err as Error
        );
      } finally {
        confirmRemoveDialog.value = false;
        removeItemsLoading.value = false;
      }
    };

    return {
      currentPage,
      currentRows,
      updatePage,
      updateRows,
      loading,
      items,
      listHeaders,
      selectedItems,
      confirmRemoveDialog,
      removeItemsLoading,
      removeListItems,
      addUrl,
      itemLink,
      confirmRemoveMessage,
    };
  },
});
</script>
